import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  Circle,
  Lock,
  Play,
  Clock,
  Award,
  ChevronRight,
  BookOpen,
  Target,
  TrendingUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  ConceptCategory,
  Difficulty,
  NodeStatus,
  BEGINNER_PATH,
  GODOT_BEGINNER_PATH,
  PYJS_BEGINNER_PATH,
  DEFAULT_REQUIRED_SUCCESSFUL_EXERCISES,
  getPrebuiltExercisesForNode
} from '@unity-learning/shared';
import type {
  LearningPath as LearningPathType,
  LearningNode,
  LearningNodeProgress,
  Milestone
} from '@unity-learning/shared';
import { useLearningMode } from '../contexts/LearningModeContext';
import { useUser } from '../hooks/useUser';
import { getLearningPathProgress, getNodeKnowledgePoints, initializeLearningPath } from '../services/api';

// Plus de copie inline du curriculum : parcours, nœuds, jalons et types viennent de
// `shared` (BEGINNER_PATH = les 17 nœuds Unity + UNITY_MILESTONES ; GODOT_BEGINNER_PATH ;
// PYJS_BEGINNER_PATH). Ne jamais re-déclarer un nœud ici (piège n°2 d'AGENTS.md).
// L'objectif « X/N exercices réussis » (requiredSuccessfulExercises, ou DEFAULT_REQUIRED_SUCCESSFUL_EXERCISES)
// est AUSSI le seuil de complétion réel du nœud côté backend (getNodeRequirements) : une seule valeur.

// Color mapping system for knowledge point tags
const TAG_COLORS = [
  'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800',
  'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800',
  'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300 border-pink-200 dark:border-pink-800',
  'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800',
  'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
  'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800',
  'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800',
  'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800',
  'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800',
  'bg-lime-100 dark:bg-lime-900/30 text-lime-800 dark:text-lime-300 border-lime-200 dark:border-lime-800',
  'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
];

function getTagColor(tag: string, nodeId: string): string {
  // Get unique tags for this node to ensure consistent coloring
  const tagHash = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const nodeHash = nodeId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

  // Combine tag and node hash to get consistent color assignment
  const colorIndex = (tagHash + nodeHash) % TAG_COLORS.length;
  return TAG_COLORS[colorIndex];
}

interface LearningPathProps {
  onStartExercise: (concepts: ConceptCategory[], difficulty: Difficulty, nodeId: string, knowledgePointIds?: string[]) => void;
  // B1 — « Mini-cours d'abord » : entrée secondaire sur chaque nœud. Optionnel pour ne pas
  // casser les autres usages du composant. Au clic, HomePage génère l'exercice du nœud puis
  // navigue vers /mini-cours/:exerciseId au lieu de /exercise/:exerciseId.
  onStartMiniCours?: (nodeId: string) => void;
  isGenerating: boolean;
}

interface KnowledgePoint {
  id: string;
  code: string;
  name: string;
  description: string;
  tag: string;
  node_id: string;
  requirement_successes: number;
  active: boolean;
  attempts_count: number;
  successes_count: number;
  best_score: number;
  first_success_at: string | null;
  last_attempt_at: string | null;
}

interface LearningNodeWithKP extends NodeProgressInfo {
  knowledgePoints: KnowledgePoint[];
  knowledgePointsLoaded: boolean;
}

interface NodeProgressInfo extends Omit<LearningNode, 'requiredSuccessfulExercises'> {
  requiredSuccessfulExercises: number; // résolu (valeur du nœud ou DEFAULT_REQUIRED_SUCCESSFUL_EXERCISES)
  status: NodeStatus;
  progress: LearningNodeProgress | null;
  isRecommended: boolean;
}

const LearningPath: React.FC<LearningPathProps> = ({ onStartExercise, onStartMiniCours, isGenerating }) => {
  const { user } = useUser();
  const { isPythonJsMode, isGodotMode, learningMode } = useLearningMode();
  const navigate = useNavigate();
  const [selectedPath, setSelectedPath] = useState<LearningPathType | null>(null);
  const [userProgress, setUserProgress] = useState<LearningNodeProgress[]>([]);
  const [pathProgress, setPathProgress] = useState<LearningNodeWithKP[]>([]);
  const [expandedPath, setExpandedPath] = useState<string | null>(isPythonJsMode ? 'beginner_python_js_programmer' : isGodotMode ? 'beginner_godot_programmer' : 'beginner_unity_programmer');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Load learning paths and user progress from API
  useEffect(() => {
    if (!user) return;

    const loadLearningPathData = async () => {
      try {
        console.log('🔄 Initialisation parcours d\'apprentissage pour utilisateur:', user.id);

        // Initialize user's learning path if needed (idempotent) ; un échec n'empêche pas
        // de lire la progression existante.
        try {
          await initializeLearningPath(user.id);
          console.log('✅ Parcours d\'apprentissage initialisé');
        } catch (initError) {
          console.warn('⚠️ Erreur initialisation:', (initError as Error).message);
        }

        // Load user's progress
        console.log('🔄 Chargement progression utilisateur...');
        const progress = await getLearningPathProgress(user.id);
        console.log('📊 Progression chargée:', progress);
        setUserProgress(progress);
      } catch (error) {
        console.error('❌ Erreur chargement données parcours d\'apprentissage:', error);
      }
    };

    loadLearningPathData();

    // Parcours du mode courant — source unique : `shared` (plus de copie inline).
    const beginnerPath: LearningPathType = isPythonJsMode
      ? PYJS_BEGINNER_PATH
      : isGodotMode
        ? GODOT_BEGINNER_PATH
        : BEGINNER_PATH;

    setExpandedPath(beginnerPath.id);

    // Auto-select beginner path for new users or when mode changes
    console.log('🛤️ Sélection du parcours:', beginnerPath.name);
    setSelectedPath(beginnerPath);
  }, [user, isPythonJsMode, isGodotMode]); // Re-run when mode changes

  // Calculate node progress and status
  useEffect(() => {
    if (!selectedPath || !user) return;

    const progressInfo: LearningNodeWithKP[] = selectedPath.nodes.map((node: LearningNode) => {
      const progress = userProgress.find((p: LearningNodeProgress) => p.nodeId === node.id) || null;

      // Determine status based on prerequisites and progress
      let status = NodeStatus.LOCKED;

      if (node.prerequisites.length === 0) {
        status = NodeStatus.AVAILABLE; // First nodes are always available
      } else {
        // Check if all prerequisites are completed
        const prerequisitesCompleted = node.prerequisites.every((preqId: string) =>
          userProgress.some((p: LearningNodeProgress) => p.nodeId === preqId && p.status === NodeStatus.COMPLETED)
        );

        if (prerequisitesCompleted) {
          status = NodeStatus.AVAILABLE;
        }
      }

      if (progress) {
        status = progress.status;
      }

      // Determine if this is the recommended next step
      const isRecommended = status === NodeStatus.AVAILABLE &&
        !userProgress.some((p: LearningNodeProgress) => p.nodeId === node.id);

      return {
        ...node,
        requiredSuccessfulExercises: node.requiredSuccessfulExercises ?? DEFAULT_REQUIRED_SUCCESSFUL_EXERCISES,
        status,
        progress,
        isRecommended,
        knowledgePoints: [], // Will be loaded separately
        knowledgePointsLoaded: false
      };
    });

    setPathProgress(progressInfo);
  }, [selectedPath, userProgress, user]);

  // Load knowledge points for a specific node
  const loadKnowledgePoints = async (nodeId: string) => {
    if (!user) return;

    try {
      console.log('🔄 Loading knowledge points for node:', nodeId);
      const knowledgePoints = await getNodeKnowledgePoints<KnowledgePoint>(nodeId, user.id);
      console.log(`📊 Loaded ${knowledgePoints.length} knowledge points for node ${nodeId}`);

      // Update the specific node in pathProgress
      setPathProgress(prev => prev.map(node =>
        node.id === nodeId
          ? { ...node, knowledgePoints, knowledgePointsLoaded: true }
          : node
      ));
    } catch (error) {
      console.error('❌ Error loading knowledge points:', error);
    }
  };

  const handleStartNode = (node: LearningNodeWithKP) => {
    if (node.status === NodeStatus.LOCKED || isGenerating) return;

    onStartExercise(node.concepts, node.difficulty, node.id);
  };

  const handleStartKnowledgePoint = (node: LearningNodeWithKP, knowledgePoint: KnowledgePoint) => {
    if (node.status === NodeStatus.LOCKED || isGenerating) return;

    onStartExercise(node.concepts, node.difficulty, node.id, [knowledgePoint.id]);
  };

  const toggleNodeExpansion = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);

    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);

      // Load knowledge points when node is expanded for the first time
      const node = pathProgress.find(n => n.id === nodeId);
      if (node && !node.knowledgePointsLoaded) {
        loadKnowledgePoints(nodeId);
      }
    }

    setExpandedNodes(newExpanded);
  };

  const getStatusIcon = (status: NodeStatus, isRecommended: boolean) => {
    switch (status) {
      case NodeStatus.COMPLETED:
      case NodeStatus.MASTERED:
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case NodeStatus.IN_PROGRESS:
        return <Circle className="w-5 h-5 text-blue-600 animate-pulse" />;
      case NodeStatus.AVAILABLE:
        return isRecommended ?
          <Target className="w-5 h-5 text-orange-600" /> :
          <Circle className="w-5 h-5 text-gray-400" />;
      case NodeStatus.LOCKED:
      default:
        return <Lock className="w-5 h-5 text-gray-300 dark:text-gray-600" />;
    }
  };

  const getStatusColor = (status: NodeStatus, isRecommended: boolean) => {
    switch (status) {
      case NodeStatus.COMPLETED:
      case NodeStatus.MASTERED:
        return 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20';
      case NodeStatus.IN_PROGRESS:
        return 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20';
      case NodeStatus.AVAILABLE:
        return isRecommended ?
          'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 ring-2 ring-orange-200 dark:ring-orange-800' :
          'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700';
      case NodeStatus.LOCKED:
      default:
        return 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 opacity-60';
    }
  };

  const completedNodes = pathProgress.filter((n: LearningNodeWithKP) =>
    n.status === NodeStatus.COMPLETED || n.status === NodeStatus.MASTERED
  ).length;
  const totalNodes = pathProgress.length;
  const progressPercentage = totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0;
  const nextRecommended = pathProgress.find((n: LearningNodeWithKP) => n.isRecommended);

  if (!selectedPath) {
    return <div className="text-center p-4">Chargement des parcours d'apprentissage...</div>;
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg shadow-sm border border-blue-200 dark:border-blue-800 p-6 mb-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <BookOpen className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400" />
            Parcours d'Apprentissage
          </h2>
          <button
            onClick={() => setExpandedPath(expandedPath === selectedPath.id ? null : selectedPath.id)}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
          >
            {expandedPath === selectedPath.id ? 'Réduire' : 'Voir détails'}
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-100 dark:border-blue-900">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">{selectedPath.name}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{selectedPath.description}</p>

          {/* Progress Bar */}
          <div className="flex items-center space-x-3">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {completedNodes}/{totalNodes} complété
            </span>
          </div>
        </div>

        {/* Next Recommended Step */}
        {nextRecommended && (
          <div className="mt-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Target className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2" />
                <div>
                  <h4 className="font-medium text-orange-900 dark:text-orange-100">Étape suivante recommandée</h4>
                  <p className="text-sm text-orange-700 dark:text-orange-300">{nextRecommended.title}</p>
                </div>
              </div>
              <button
                onClick={() => handleStartNode(nextRecommended)}
                disabled={isGenerating}
                className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50"
              >
                <div className="flex items-center">
                  <Play className="w-4 h-4 mr-1" />
                  Commencer
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Learning Nodes */}
      {expandedPath === selectedPath.id && (
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Étapes du parcours :</h4>
          {pathProgress.map((node, index) => (
            <div
              key={node.id}
              className={`border rounded-lg p-4 transition-all duration-200 ${getStatusColor(node.status, node.isRecommended)}`}
            >
              <div className="flex items-start space-x-3">
                {/* Status Icon */}
                <div className="flex-shrink-0 mt-1">
                  {getStatusIcon(node.status, node.isRecommended)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-gray-900 dark:text-white">{node.title}</h5>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <Clock className="w-4 h-4 mr-1" />
                      {node.estimatedTime}min
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{node.description}</p>

                  {/* Prerequisites */}
                  {node.prerequisites.length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Prérequis: {node.prerequisites.map((preqId: string) => {
                        const preqNode = pathProgress.find((n: LearningNodeWithKP) => n.id === preqId);
                        return preqNode?.title;
                      }).join(', ')}
                    </div>
                  )}

                  {/* Progress Info */}
                  {node.progress ? (
                    <div className="space-y-1 mb-3">
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium text-blue-600 dark:text-blue-400">
                          {node.progress.successfulExercisesCount || 0}/{node.requiredSuccessfulExercises} exercices réussis
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">
                          • {node.progress.attemptsCount} tentatives au total
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        Meilleur score: <span className="font-medium text-green-600 dark:text-green-400">{node.progress.bestScore}%</span>
                      </div>
                      {/* Progress bar for completion */}
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(100, ((node.progress.successfulExercisesCount || 0) / node.requiredSuccessfulExercises) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {Math.round(((node.progress.successfulExercisesCount || 0) / node.requiredSuccessfulExercises) * 100)}%
                        </span>
                      </div>
                    </div>
                  ) : node.status !== NodeStatus.LOCKED ? (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      {node.requiredSuccessfulExercises ? (
                        <span>Objectif: {node.requiredSuccessfulExercises} exercices à réussir</span>
                      ) : (
                        <span>Pas encore de données de progression</span>
                      )}
                    </div>
                  ) : null}

                  {/* Knowledge Points Section */}
                  {node.status !== NodeStatus.LOCKED && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h6 className="text-sm font-medium text-gray-800 dark:text-gray-200">Points de Connaissance</h6>
                        <button
                          onClick={() => toggleNodeExpansion(node.id)}
                          className={`px-4 py-2 rounded font-semibold text-sm transition-all duration-200 shadow-sm ${expandedNodes.has(node.id)
                            ? 'bg-slate-400 hover:bg-slate-500 text-white'
                            : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white transform hover:scale-105'
                            }`}
                        >
                          {expandedNodes.has(node.id) ? '🔼 Masquer' : '🔍 Voir détails'}
                        </button>
                      </div>

                      {/* Knowledge Points Grid */}
                      {expandedNodes.has(node.id) && (
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          {!node.knowledgePointsLoaded ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                              Chargement des points de connaissance...
                            </div>
                          ) : node.knowledgePoints.length === 0 ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                              Aucun point de connaissance défini pour cette étape
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {node.knowledgePoints.map((kp) => {
                                const isCompleted = kp.successes_count >= kp.requirement_successes;
                                const progressPercent = Math.min(100, (kp.successes_count / kp.requirement_successes) * 100);

                                return (
                                  <div
                                    key={kp.id}
                                    className={`p-2 rounded border text-xs transition-all ${isCompleted
                                      ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                                      : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-700'
                                      }`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-gray-800 dark:text-gray-200 truncate pr-1">
                                        {kp.name}
                                      </span>
                                      {isCompleted && (
                                        <CheckCircle2 className="w-3 h-3 text-green-600 dark:text-green-400 flex-shrink-0" />
                                      )}
                                    </div>

                                    {kp.tag && (
                                      <div className="mb-2">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getTagColor(kp.tag, node.id)}`}>
                                          {kp.tag}
                                        </span>
                                      </div>
                                    )}

                                    <p className="text-gray-600 dark:text-gray-400 text-xs mb-2 line-clamp-2">
                                      {kp.description}
                                    </p>

                                    <div className="flex items-center justify-between">
                                      <div className="flex-1 mr-2">
                                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
                                          <span>{kp.successes_count}/{kp.requirement_successes}</span>
                                          {kp.best_score > 0 && (
                                            <span className="ml-2">• {kp.best_score}%</span>
                                          )}
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                                          <div
                                            className={`h-1 rounded-full transition-all ${isCompleted ? 'bg-green-500' : 'bg-blue-500'
                                              }`}
                                            style={{ width: `${progressPercent}%` }}
                                          />
                                        </div>
                                      </div>

                                      <button
                                        onClick={() => handleStartKnowledgePoint(node, kp)}
                                        disabled={isGenerating}
                                        className={`px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${isCompleted
                                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                                          }`}
                                      >
                                        {isCompleted ? 'Refaire' : 'Pratiquer'}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="flex justify-end items-center gap-2 flex-wrap">
                    {/* Exercices pré-créés (simulateur) rattachés au nœud : bouton distinct de
                        « Commencer »/« Pratiquer » (génération IA). Masqué tant que le nœud est verrouillé.
                        La page cible /exercise/:id?learningNodeId= est rendue par SimulatorExercise via ExercisePage. */}
                    {node.status !== NodeStatus.LOCKED && getPrebuiltExercisesForNode(learningMode, node.id).map((prebuilt) => (
                      <button
                        key={prebuilt.id}
                        type="button"
                        onClick={() => navigate(`/exercise/${prebuilt.id}?learningNodeId=${encodeURIComponent(node.id)}`)}
                        title={prebuilt.title}
                        aria-label={prebuilt.title}
                        className="inline-flex items-center px-2.5 py-1 rounded text-sm font-medium border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                      >
                        🎮 {prebuilt.shortLabel}
                      </button>
                    ))}
                    {/* B1 — « Mini-cours d'abord » : entrée secondaire discrète (ghost button)
                        pour ne pas parasiter le bouton principal « Commencer ». */}
                    {onStartMiniCours && node.status !== NodeStatus.LOCKED && (
                      <button
                        onClick={() => onStartMiniCours(node.id)}
                        disabled={isGenerating}
                        title="Mini-cours d'abord"
                        aria-label="Mini-cours d'abord"
                        className="inline-flex items-center px-2.5 py-1 rounded text-sm font-medium text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors disabled:opacity-50"
                      >
                        <BookOpen className="w-4 h-4 mr-1" />
                        Mini-cours
                      </button>
                    )}
                    <button
                      onClick={() => handleStartNode(node)}
                      disabled={node.status === NodeStatus.LOCKED || isGenerating}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${node.status === NodeStatus.LOCKED
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : node.isRecommended
                          ? 'bg-orange-600 hover:bg-orange-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'
                        }`}
                    >
                      <div className="flex items-center">
                        {node.status === NodeStatus.LOCKED ? (
                          <Lock className="w-3 h-3 mr-1" />
                        ) : (
                          <Play className="w-3 h-3 mr-1" />
                        )}
                        {node.status === NodeStatus.LOCKED
                          ? 'Verrouillé'
                          : node.status === NodeStatus.COMPLETED
                            ? 'Refaire'
                            : 'Commencer'
                        }
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Milestones */}
      {selectedPath.milestones && selectedPath.milestones.length > 0 && (
        <div className="mt-6 pt-6 border-t border-blue-200 dark:border-blue-800">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
            <Award className="w-5 h-5 mr-2 text-yellow-600 dark:text-yellow-400" />
            Jalons et Récompenses
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {selectedPath.milestones.map((milestone: Milestone) => {
              const isUnlocked = milestone.requiredNodes.every((nodeId: string) =>
                pathProgress.some((n: NodeProgressInfo) => n.id === nodeId && n.status === NodeStatus.COMPLETED)
              );

              return (
                <div
                  key={milestone.id}
                  className={`p-3 rounded-lg border ${isUnlocked
                    ? 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                    }`}
                >
                  <div className="flex items-center mb-1">
                    <Award className={`w-4 h-4 mr-2 ${isUnlocked ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    <h5 className={`font-medium ${isUnlocked ? 'text-yellow-900 dark:text-yellow-100' : 'text-gray-600 dark:text-gray-300'}`}>
                      {milestone.name}
                    </h5>
                  </div>
                  <p className={`text-sm ${isUnlocked ? 'text-yellow-800 dark:text-yellow-200' : 'text-gray-500 dark:text-gray-400'}`}>
                    {milestone.description}
                  </p>
                  {milestone.reward && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      🏆 {milestone.reward}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningPath;
